import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { FormControl, FormField, FormItem } from "design-system/organisms/Form"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { cn } from "design-system/utils"
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react"
import { useMemo } from "react"
import { useFieldArray, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WebhookBodyFormat, WebhookMethod } from "schema"
import { VariableOption } from "../../../hooks/useCapabilityVariables"
import { TestWebhookDialog } from "./TestWebhookDialog"
import { WebhookRequestFields } from "./WebhookRequestFields"
import { isWebhookRequestInvalid, toFieldPath, WebhookConfig } from "./WebhookRequestUtils"

type Props = {
  form: UseFormReturn<WebhookConfig>
  variables?: VariableOption[]
}

function parseStepVariables(
  responseBody: string | null | undefined,
  stepIndex: number,
): VariableOption[] {
  if (!responseBody) return []

  try {
    const parsed: unknown = JSON.parse(responseBody)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return []

    return Object.keys(parsed as Record<string, unknown>).map((key) => ({
      value: `steps[${stepIndex}].${key}`,
      label: `steps[${stepIndex}].${key}`,
    }))
  } catch {
    return []
  }
}

export function WebhookAccordion({ form, variables = [] }: Props) {
  const { t } = useTranslation()
  const mode = form.watch("mode") ?? "single"

  const url = form.watch("url")
  const method = form.watch("method")
  const webhookHeaders = form.watch("webhookHeaders") ?? []
  const webhookBody = form.watch("webhookBody") ?? "{}"
  const webhookBodyFormat = form.watch("webhookBodyFormat") ?? WebhookBodyFormat.json

  const isTestWebhookDisabled = useMemo(
    () => isWebhookRequestInvalid(url, method, webhookBody, webhookBodyFormat),
    [url, method, webhookBody, webhookBodyFormat],
  )

  const { fields, append, remove, move, replace } = useFieldArray({
    control: form.control,
    name: "steps",
  })
  const watchedSteps = form.watch("steps")
  const steps = useMemo(() => watchedSteps ?? [], [watchedSteps])

  function isStepTestDisabled(index: number) {
    const step = steps[index]
    if (
      !step ||
      isWebhookRequestInvalid(step.url, step.method, step.webhookBody, step.webhookBodyFormat)
    )
      return true

    for (let priorIndex = 0; priorIndex < index; priorIndex++) {
      if (!steps[priorIndex]?.testResponseBody) return true
    }

    return false
  }

  function priorStepResponses(index: number) {
    return steps
      .slice(0, index)
      .map((step) => step.testResponseBody)
      .filter((response): response is string => Boolean(response))
  }

  function stepVariables(index: number): VariableOption[] {
    const priorVariables = Array.from({ length: index }, (_, stepIndex) =>
      parseStepVariables(steps[stepIndex]?.testResponseBody, stepIndex),
    ).flat()

    return [...variables, ...priorVariables]
  }

  function createEmptyStep() {
    return {
      url: "",
      method: WebhookMethod.POST,
      webhookHeaders: [],
      webhookBody: "{}",
      webhookBodyFormat: WebhookBodyFormat.json,
    }
  }

  function handleAddStep() {
    append(createEmptyStep())
  }

  function handleModeChange(isChainMode: boolean) {
    form.setValue("mode", isChainMode ? "chain" : "single", { shouldDirty: true })

    if (!isChainMode) return

    if (fields.length === 0) {
      const { url, method, webhookHeaders, webhookBody, webhookBodyFormat, testResponseBody } =
        form.getValues()
      replace([
        {
          url: url ?? "",
          method: method ?? WebhookMethod.POST,
          webhookHeaders: webhookHeaders ?? [],
          webhookBody: webhookBody ?? "{}",
          webhookBodyFormat: webhookBodyFormat ?? WebhookBodyFormat.json,
          testResponseBody: testResponseBody ?? null,
        },
        createEmptyStep(),
      ])
      return
    }

    const missingSteps = Math.max(2 - fields.length, 0)
    for (let i = 0; i < missingSteps; i++) {
      handleAddStep()
    }
  }

  return (
    <Accordion
      type="single"
      defaultValue="webhook"
      collapsible
      className={cn(
        "border rounded-lg bg-secondary",
        (form.formState.errors.webhookHeaders ||
          form.formState.errors.url ||
          form.formState.errors.steps) &&
          "border-destructive",
      )}
    >
      <AccordionItem value="webhook">
        <AccordionTrigger className="text-lg">
          {t("capabilities.webhookConfig.webhook")}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-5">
          {mode === "single" && (
            <>
              <WebhookRequestFields form={form} namePrefix="" variables={variables} />
              <TestWebhookDialog
                formValues={{
                  url: url ?? "",
                  method: method ?? WebhookMethod.POST,
                  webhookHeaders,
                  webhookBody,
                  webhookBodyFormat,
                }}
                disabled={isTestWebhookDisabled}
                onSuccess={(webhookResponse) => {
                  form.setValue("testResponseBody", webhookResponse)
                }}
                onError={(error) => {
                  form.setError("testResponseBody", {
                    message: error instanceof Error ? error.message : String(error),
                  })
                }}
              />
              {form.watch("testResponseBody") && (
                <Accordion type="single" collapsible className="border rounded-lg bg-muted">
                  <AccordionItem value="saved-response" className="border-none">
                    <AccordionTrigger className="text-sm px-4 py-3">
                      {t("capabilities.webhookConfig.testWebhook.savedResponse")}
                    </AccordionTrigger>
                    <AccordionContent className="min-w-0 px-4 pb-4 overflow-auto">
                      <MarkdownRenderer
                        className="min-w-0 max-w-full overflow-auto"
                        cardClassName="border-none shadow-none min-w-0 max-w-full"
                        codeClassName="border-none whitespace-pre-wrap break-all"
                        shortenTextAfterLength={0}
                      >
                        {`\`\`\`json\n${form.watch("testResponseBody")}\n\`\`\``}
                      </MarkdownRenderer>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </>
          )}

          {mode === "chain" && (
            <div className="flex flex-col gap-4">
              {fields.map((field, index) => (
                <div key={field.id} className="flex flex-col gap-3 border rounded-lg p-4 bg-muted">
                  <div className="flex flex-row items-center justify-between gap-2">
                    <p className="text-sm font-medium">
                      {t("capabilities.webhookConfig.chain.stepTitle", { number: index + 1 })}
                    </p>
                    <div className="flex flex-row items-center gap-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      >
                        <ChevronUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={index === fields.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        <ChevronDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={fields.length <= 2}
                        onClick={() => remove(index)}
                        className={cn(fields.length <= 2 && "hidden")}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <WebhookRequestFields
                    form={form}
                    namePrefix={`steps.${index}.`}
                    variables={stepVariables(index)}
                  />
                  <TestWebhookDialog
                    formValues={{
                      url: steps[index]?.url ?? "",
                      method: steps[index]?.method ?? WebhookMethod.POST,
                      webhookHeaders: steps[index]?.webhookHeaders ?? [],
                      webhookBody: steps[index]?.webhookBody ?? "{}",
                      webhookBodyFormat: steps[index]?.webhookBodyFormat ?? WebhookBodyFormat.json,
                    }}
                    priorResponses={priorStepResponses(index)}
                    disabled={isStepTestDisabled(index)}
                    onSuccess={(webhookResponse) => {
                      form.setValue(
                        toFieldPath(`steps.${index}.`, "testResponseBody"),
                        webhookResponse,
                      )
                    }}
                    onError={(error) => {
                      form.setError(toFieldPath(`steps.${index}.`, "testResponseBody"), {
                        message: error instanceof Error ? error.message : String(error),
                      })
                    }}
                  />
                  {steps[index]?.testResponseBody && (
                    <Accordion type="single" collapsible className="border rounded-lg bg-secondary">
                      <AccordionItem value={`saved-response-${index}`} className="border-none">
                        <AccordionTrigger className="text-sm px-4 py-3">
                          {t("capabilities.webhookConfig.testWebhook.savedResponse")}
                        </AccordionTrigger>
                        <AccordionContent className="min-w-0 px-4 pb-4 overflow-auto">
                          <MarkdownRenderer
                            className="min-w-0 max-w-full overflow-auto"
                            cardClassName="border-none shadow-none min-w-0 max-w-full"
                            codeClassName="border-none whitespace-pre-wrap break-all"
                            shortenTextAfterLength={0}
                          >
                            {`\`\`\`json\n${steps[index]?.testResponseBody}\n\`\`\``}
                          </MarkdownRenderer>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </div>
              ))}
              {fields.length < 2 && (
                <p className="text-sm text-destructive">
                  {t("capabilities.webhookConfig.chain.minSteps")}
                </p>
              )}
              <div className="flex flex-row gap-2">
                <Button type="button" variant="outline" onClick={handleAddStep}>
                  <Plus className="size-4" />
                  {t("capabilities.webhookConfig.chain.addStep")}
                </Button>
              </div>
            </div>
          )}
          <FormField
            control={form.control}
            name="mode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex flex-row items-center justify-between gap-1">
                    <div className="text-sm text-muted-foreground">
                      {t("capabilities.webhookConfig.chain.description")}
                    </div>
                    <div className="flex flex-col items-end gap-2 w-1/3">
                      <Label
                        htmlFor="chain-mode-switch"
                        className={cn(field.value !== "chain" && "text-muted-foreground")}
                      >
                        {t("capabilities.webhookConfig.chain.label")}
                      </Label>
                      <Switch
                        id="chain-mode-switch"
                        checked={field.value === "chain"}
                        onCheckedChange={handleModeChange}
                      />
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
